import React from 'react';
import PropTypes from 'prop-types';
import { t, Trans } from '@lingui/macro';

import { Flex, H4, Modal, P } from '@decisiv/ui-components';
import InfoCircle from '@decisiv/iconix/lib/components/InfoCircle';
import { JsonEditor } from 'json-edit-react';

import { CODE_EDITOR_THEME } from '../../theme/jsonEditorTheme';
import { DeveloperCodeView } from '../../styles/DeveloperCodeView';
import { InfoPanel, LineDivider } from './style';

const SAMPLE_PROVIDER = 'Volvo';

const humanizeTemplate = (template) =>
  template ? template.replace(/%<provider>s/g, '<Provider>') : '—';

const yesNo = (isError, flag) => {
  if (!isError) return '—';

  return flag ? t`Yes` : t`No`;
};

// Builds a representative response for the entry, so consumers can see the
// exact JSON shape they will receive for this error type.
const buildExample = (attributes) => {
  const {
    kind,
    error_type: errorType,
    title,
    detail_template: detailTemplate,
    default_status: defaultStatus,
    code,
    class_name: className,
  } = attributes;

  if (kind !== 'error') {
    if (errorType === 'no-data') {
      return { payload: { campaigns: [] }, errors: [] };
    }

    return {
      payload: { contracts: ['…'] },
      errors: [
        {
          title: 'Provider Error',
          detail: `Unable to retrieve this data from ${SAMPLE_PROVIDER}. Please consult the appropriate OEM system directly.`,
          meta: { error_type: 'upstream-failure' },
        },
      ],
    };
  }

  return {
    title,
    detail: detailTemplate
      ? detailTemplate.replace(/%<provider>s/g, SAMPLE_PROVIDER)
      : null,
    status: defaultStatus,
    code,
    meta: {
      type: className,
      error_type: errorType,
      provider: SAMPLE_PROVIDER,
      diagnostic: '…raw provider/technical detail, for support only…',
    },
  };
};

const Entry = ({ label, children = null }) => (
  <Flex>
    <P color="charcoal" weight="semibold">
      {label}&nbsp;
    </P>
    <P weight="regular" color="alaskanHusky">
      {children}
    </P>
  </Flex>
);

Entry.propTypes = {
  label: PropTypes.node.isRequired,
  children: PropTypes.node,
};
export default function CommandErrorDetailModal({
  commandError = null,
  onClose,
}) {
  const attributes = commandError?.attributes;

  if (!attributes) return null;

  const isError = attributes.kind === 'error';
  const statuses = attributes.statuses || [];

  return (
    <Modal
      actions={[{ text: t`Close`, onClick: onClose, kind: 'secondary' }]}
      icon={InfoCircle}
      title={attributes.title}
      visible
      onClose={onClose}
    >
      <Flex flexDirection="column">
        <InfoPanel>
          <P weight="semibold">
            <Trans>DETAILS</Trans>
          </P>
          <Entry label={<Trans>Type:</Trans>}>{attributes.error_type}</Entry>
          <Entry label={<Trans>Kind:</Trans>}>{attributes.kind}</Entry>
          <Entry label={<Trans>Description:</Trans>}>
            {attributes.description}
          </Entry>
          <Entry label={<Trans>User message:</Trans>}>
            {humanizeTemplate(attributes.detail_template)}
          </Entry>
          <Entry label={<Trans>Statuses:</Trans>}>
            {statuses.length > 0 ? statuses.join(', ') : '—'}
          </Entry>
          <Entry label={<Trans>Code:</Trans>}>{attributes.code || '—'}</Entry>
          <Entry label={<Trans>Retryable:</Trans>}>
            {yesNo(isError, attributes.retryable)}
          </Entry>
          <Entry label={<Trans>Notifies support:</Trans>}>
            {yesNo(isError, attributes.notifies_support)}
          </Entry>
        </InfoPanel>

        <LineDivider />

        <Flex marginTop={2} flexDirection="column">
          <H4>
            {isError ? (
              <Trans>Example error</Trans>
            ) : (
              <Trans>Example response</Trans>
            )}
          </H4>
          <Flex marginTop={1} data-testid="command-error-example">
            <DeveloperCodeView>
              <JsonEditor
                viewOnly
                rootName=""
                theme={CODE_EDITOR_THEME}
                data={buildExample(attributes)}
              />
            </DeveloperCodeView>
          </Flex>
        </Flex>
      </Flex>
    </Modal>
  );
}

CommandErrorDetailModal.propTypes = {
  commandError: PropTypes.shape({
    id: PropTypes.string,
    attributes: PropTypes.shape({
      error_type: PropTypes.string,
      kind: PropTypes.string,
      code: PropTypes.string,
      class_name: PropTypes.string,
      title: PropTypes.string,
      detail_template: PropTypes.string,
      default_status: PropTypes.number,
      statuses: PropTypes.arrayOf(PropTypes.number),
      retryable: PropTypes.bool,
      notifies_support: PropTypes.bool,
      description: PropTypes.string,
    }),
  }),
  onClose: PropTypes.func.isRequired,
};
